import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Printer } from "lucide-react";
import { formatDate } from "@/lib/date";

// Strips a unit a user may have already typed into a free-text clinical field
// (e.g. "75kg", "120/80 mmHg") so displaying our own canonical unit never doubles up.
const stripKnownUnit = (value, pattern) => {
  if (!value) return value;
  const cleaned = String(value).replace(pattern, "").trim();
  return cleaned || value;
};

// Read-only body of the "View Prescription" dialog, with quick Edit/Print
// actions at the bottom.
export function PrescriptionViewDialogBody({ prescription, navigate }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Prescription — {formatDate(prescription.created_at)}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 text-sm">
        <p className="text-slate-500">By {prescription.doctor_name}{prescription.last_edited_at && ` · last edited ${formatDate(prescription.last_edited_at)} by ${prescription.last_edited_by}`}</p>
        {(prescription.clinical_weight || prescription.clinical_bp || prescription.clinical_notes) && (
          <div>
            <p className="font-semibold text-slate-900">Clinical Examination</p>
            <p className="text-slate-700">Weight: {prescription.clinical_weight ? `${stripKnownUnit(prescription.clinical_weight, /kgs?\.?/gi)} kg` : "—"} · BP: {prescription.clinical_bp ? `${stripKnownUnit(prescription.clinical_bp, /mm\s*hg\.?/gi)} mm Hg` : "—"}</p>
            {prescription.clinical_notes && <p className="text-slate-700">Notes: {prescription.clinical_notes}</p>}
          </div>
        )}
        <div>
          <p className="font-semibold text-slate-900">Medicines</p>
          {prescription.medicines?.length > 0 ? (
            <div className="mt-1 overflow-x-auto">
              <table className="w-full text-xs" data-testid="view-prescription-medicines-table">
                <thead>
                  <tr className="border-b border-teal-100 bg-teal-50/70 text-left text-teal-800">
                    <th className="py-1.5 px-2 font-semibold">Name</th><th className="py-1.5 px-2 font-semibold">Strength</th><th className="py-1.5 px-2 font-semibold">Dosage</th><th className="py-1.5 px-2 font-semibold">Frequency</th><th className="py-1.5 px-2 font-semibold">Timing</th><th className="py-1.5 px-2 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {prescription.medicines.map((m, i) => (
                    <tr key={`${i}-${m.name}`} className="border-b border-slate-100">
                      <td className="py-1 pr-2">{m.name}</td><td className="py-1 pr-2">{m.strength}</td><td className="py-1 pr-2">{m.dosage}</td><td className="py-1 pr-2">{m.frequency}</td><td className="py-1 pr-2">{m.food_timing}</td><td className="py-1 pr-2">{m.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-slate-400">None recorded.</p>}
        </div>
        {(prescription.diet_advice || prescription.exercise_advice || prescription.lifestyle_advice) && (
          <div>
            <p className="font-semibold text-slate-900">Lifestyle Advice</p>
            {prescription.diet_advice && <p className="text-slate-700">Diet: {prescription.diet_advice}</p>}
            {prescription.exercise_advice && <p className="text-slate-700">Exercise: {prescription.exercise_advice}</p>}
            {prescription.lifestyle_advice && <p className="text-slate-700">Lifestyle: {prescription.lifestyle_advice}</p>}
          </div>
        )}
        {prescription.investigations?.length > 0 && (
          <div>
            <p className="font-semibold text-slate-900">Investigations</p>
            <p className="text-slate-700">{prescription.investigations.join(", ")}</p>
          </div>
        )}
        {prescription.followup_date && (
          <div>
            <p className="font-semibold text-slate-900">Follow-up</p>
            <p className="text-slate-700">{formatDate(prescription.followup_date)}{prescription.followup_notes && ` — ${prescription.followup_notes}`}</p>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => navigate(`/doctor/prescriptions/${prescription.id}/edit`)}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
          <Button size="sm" variant="outline" className="gap-2" onClick={() => navigate(`/doctor/prescriptions/${prescription.id}/print`)}>
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>
    </>
  );
}
